import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  GraduationCap,
  Landmark,
  Handshake,
  Building2,
  Users,
  Megaphone,
  Briefcase,
  Network,
  Globe2,
  Sparkles,
  CheckCircle,
  Calendar,
  BadgeCheck,
  MapPin,
  ShieldCheck,
  Target,
  Lightbulb,
  ChevronDown,
  ChevronUp,
  Mail,
  Phone,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * PARTNER NETWORK – Single-file React page
 * Stack: React + TailwindCSS + framer-motion + lucide-react
 * Purpose: Official gateway to invite, onboard, and support a national network of partners.
 * Drop-in sibling to your Academy, Communities, DIGIIT.AI, and Catalyst single-file apps.
 *
 * HOW TO USE
 * 1) Save as src/PartnerNetwork.jsx (or any name) in the same project as your other pages.
 * 2) Add a router entry or link to "#/partners" (see optional useHashRoute at bottom).
 * 3) Forms are client-only placeholders. Wire to your API / Formspree / Netlify later.
 */

// ================= SafeLink helpers (hash-safe smooth scroll) =================
function extractHashId(href) {
  if (!href || href[0] !== '#') return null;
  const after = href.slice(1);
  const id = after.split(/[?&]/)[0].trim();
  return id || null;
}
const SafeLink = ({ href, className = "", children, onClick=(events: any)=>{} }) => {
  const isExternal = /^(https?:)?\/\//.test(href) || href.startsWith("mailto:") || href.startsWith("tel:");
  if (isExternal) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className} onClick={onClick}>
        {children}
      </a>
    );
  }
  const handleClick = (e) => {
    e.preventDefault();
    onClick && onClick(e);
    if (href.startsWith('#')) {
      const id = extractHashId(href);
      if (id) {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      window.location.hash = href; // keep hash for history
    }
  };
  return (
    <a href={href} className={className} onClick={handleClick}>
      {children}
    </a>
  );
};

// ================= Shared UI =================
const Section = ({ id="", className = "", children }) => (
  <section id={id} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>{children}</section>
);
const Card = ({ className = "", children }) => (
  <div className={`rounded-2xl shadow-lg bg-white border border-slate-200 ${className}`}>{children}</div>
);
const H2 = ({ children, kicker, center }) => (
  <div className={`mb-8 ${center ? "text-center" : ""}`}>
    {kicker && <div className="mb-2 text-sm font-semibold tracking-widest uppercase text-indigo-600">{kicker}</div>}
    <h2 className="text-3xl md:text-4xl font-extrabold text-slate-800">{children}</h2>
  </div>
);
const Badge = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-medium text-white backdrop-blur">
    {children}
  </span>
);
const Pill = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider bg-slate-900 text-white px-3 py-1 rounded-full">
    {children}
  </span>
);

// ================= Data =================
const partnerTracks = [
  {
    key: "academia",
    title: "Academia & Institutions",
    color: "from-indigo-600 to-violet-600",
    icon: GraduationCap,
    value: ["Curriculum co-dev", "Certifications", "Labs & internships"],
  },
  {
    key: "incubators",
    title: "Incubators & Accelerators",
    color: "from-sky-500 to-cyan-600",
    icon: Landmark,
    value: ["Founder pipeline", "Cohorts & clinics", "Investor connects"],
  },
  {
    key: "consultants",
    title: "Consultants & Advisors",
    color: "from-amber-500 to-orange-600",
    icon: Briefcase,
    value: ["DPI playbooks", "BM Innovation", "Program governance"],
  },
  {
    key: "ngos",
    title: "NGOs & CSOs",
    color: "from-emerald-500 to-teal-600",
    icon: Handshake,
    value: ["Women & youth", "CSR pilots", "Inclusive growth"],
  },
  {
    key: "influencers",
    title: "Influencers & Affiliates",
    color: "from-rose-500 to-fuchsia-600",
    icon: Megaphone,
    value: ["Awareness", "Lead-gen", "Community activation"],
  },
  {
    key: "corporate",
    title: "Corporate & Enterprise",
    color: "from-slate-700 to-slate-900",
    icon: Building2,
    value: ["CSR & jobs", "Pilots & procurement", "Scale programs"],
  },
];

const stories = [
  { name: "State Partner Program", result: "50+ districts activated", blurb: "State-wide DPI adoption with clinics, cohorts, and partner toolkits." },
  { name: "Women Entrepreneurs Network", result: "700+ WEs onboarded", blurb: "Lead Women Entrepreneurs running chapters with revenue share." },
  { name: "Logistics Cluster", result: "30% faster onboarding", blurb: "ULIP + ONDC stack demoed across cluster partners and SMEs." },
  { name: "Academia Alliance", result: "1,200+ learners", blurb: "Co-branded certification with internships and live projects." },
];

const events = [
  { date: "2025-09-08", title: "Partner Onboarding Clinic (Virtual)", tag: "Onboarding" },
  { date: "2025-09-15", title: "CSR Roundtable: Women & DPI (Chennai)", tag: "CSR" },
  { date: "2025-09-28", title: "Incubator Leaders Meet (Bengaluru)", tag: "Incubators" },
  { date: "2025-10-05", title: "Academia MoU Workshop (Coimbatore)", tag: "Academia" },
];

// ================= NAV =================
// const Nav = () => {
//   const items = [
//     { href: "#home", label: "Home" },
//     { href: "#tracks", label: "Tracks" },
//     { href: "#ecosystem", label: "Ecosystem" },
//     { href: "#benefits", label: "Benefits" },
//     { href: "#stories", label: "Stories" },
//     { href: "#events", label: "Events" },
//     { href: "#join", label: "Apply" },
//     { href: "#faq", label: "FAQ" },
//   ];
//   const [open, setOpen] = useState(false);
//   return (
//     <header className="sticky top-0 z-50 bg-white/80 backdrop-blur border-b border-slate-200">
//       <Section className="flex h-16 items-center justify-between">
//         <SafeLink href="#home" className="flex items-center gap-2 font-extrabold text-slate-900 text-lg">
//           <span className="inline-grid place-items-center w-8 h-8 rounded-xl bg-indigo-600 text-white font-black">P</span>
//           Partner Network
//         </SafeLink>
//         <nav className="hidden md:flex items-center gap-6 text-sm">
//           {items.map(i => (
//             <SafeLink key={i.href} href={i.href} className="text-slate-600 hover:text-slate-900">{i.label}</SafeLink>
//           ))}
//           <SafeLink href="#join" className="inline-flex items-center gap-2 rounded-full bg-indigo-600 text-white px-4 py-2 text-sm font-semibold hover:bg-indigo-700">Become a Partner <ArrowRight className="w-4 h-4"/></SafeLink>
//         </nav>
//         <button className="md:hidden p-2" onClick={()=>setOpen(v=>!v)} aria-label="Toggle menu">
//           <span className="block w-6 h-0.5 bg-slate-900 mb-1"></span>
//           <span className="block w-6 h-0.5 bg-slate-900 mb-1"></span>
//           <span className="block w-6 h-0.5 bg-slate-900"></span>
//         </button>
//       </Section>
//       {open && (
//         <div className="md:hidden border-t border-slate-200 bg-white">
//           <Section className="py-4 flex flex-col gap-3">
//             {items.map(i => (
//               <SafeLink key={i.href} href={i.href} onClick={()=>setOpen(false)} className="text-slate-700">{i.label}</SafeLink>
//             ))}
//             <SafeLink href="#join" onClick={()=>setOpen(false)} className="inline-flex items-center justify-center gap-2 rounded-full bg-indigo-600 text-white px-4 py-2 text-sm font-semibold hover:bg-indigo-700">Become a Partner <ArrowRight className="w-4 h-4"/></SafeLink>
//           </Section>
//         </div>
//       )}
//     </header>
//   );
// };

const Nav = () => {

  const submenus = [
    // { href: "#home", label: "Home"},
    { href: "#tracks", label: "Tracks" },
    { href: "#ecosystem", label: "Ecosystem" },
    { href: "#benefits", label: "Benefits" },
    { href: "#stories", label: "Stories" },
    { href: "#events", label: "Events" },
    { href: "#join", label: "Apply" },
    { href: "#faq", label: "FAQ" },
  ];

  const [openMobile, setOpenMobile] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(false);

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur border-b border-slate-200">
              <Section id="one-digiit" className="flex flex-row h-12 w-full items-center justify-right">
                {/* Second row: submenus (desktop only) */}
                <div className="hidden md:block flex flex-row w-full items-center justify-right">
                  <Section
                    id="nav-sub"
                    className="flex items-center gap-4 py-2 overflow-x-auto justify-end"
                  >
                    {submenus.map((i) => (
                      <SafeLink
                        key={i.href}
                        href={i.href}
                        className="text-sm text-slate-600 hover:text-slate-900 whitespace-nowrap"
                      >
                        {i.label}
                      </SafeLink>
                    ))}
        
                    {/* CTA */}
                    <SafeLink
                      href="#contact"
                      className="hidden md:inline-flex items-center gap-2 rounded-full bg-amber-500 text-white px-4 py-2 text-sm font-semibold hover:bg-amber-600"
                    >
                      Talk to Us <ArrowRight className="w-4 h-4" />
                    </SafeLink>
                  </Section>
                </div>
        
        
                {/* Mobile menu button */}
                <button
                  className="md:hidden p-2"
                  onClick={() => setOpenMobile((v) => !v)}
                  aria-label="Toggle menu"
                >
                  <span className="block w-6 h-0.5 bg-slate-900 mb-1"></span>
                  <span className="block w-6 h-0.5 bg-slate-900 mb-1"></span>
                  <span className="block w-6 h-0.5 bg-slate-900"></span>
                </button>
              </Section>
        
              {/* Mobile dropdown */}
              {openMobile && (
                <div className="md:hidden border-t border-slate-200 bg-white">
                  <Section id={''} className="py-4 flex flex-col gap-3">
                    <div className="mt-4 border-t border-slate-200 pt-4">
                      {submenus.map((i) => (
                        <SafeLink
                          key={i.href}
                          href={i.href}
                          onClick={() => setOpenMobile(false)}
                          className="text-slate-600"
                        >
                          {i.label}
                        </SafeLink>
                      ))}
                    </div>
                    <SafeLink
                      href="#contact"
                      onClick={() => setOpenMobile(false)}
                      className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 text-white px-4 py-2 text-sm font-semibold hover:bg-amber-600"
                    >
                      Talk to Us <ArrowRight className="w-4 h-4" />
                    </SafeLink>
                  </Section>
                </div>
              )}
            </header>
  );
};


// ================= HERO =================
const Hero = () => (
  <div id="home" className="relative overflow-hidden bg-gradient-to-b from-indigo-800 via-indigo-800 to-violet-700 text-white">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.08),transparent_40%),radial-gradient(circle_at_85%_15%,rgba(255,255,255,0.06),transparent_35%)]"/>
    <Section className="relative py-16 md:py-24">
      <div className="grid lg:grid-cols-2 gap-10 items-center">
        <motion.div initial={{opacity:0, y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.5}}>
          <div className="flex gap-2 mb-4">
            <Badge>Nation‑scale</Badge>
            <Badge>DPI‑led</Badge>
            <Badge>Ecosystem First</Badge>
          </div>
          <h1 className="text-4xl md:text-6xl font-black leading-tight">Join the ONE Partner Network</h1>
          <p className="mt-4 text-lg md:text-xl text-indigo-100 max-w-2xl">Invite, onboard, and support partners who co‑create a platform‑driven digital future for India — across Communities, Academy, DIGIIT.AI Cloud, and Catalyst.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <SafeLink href="#tracks" className="inline-flex items-center gap-2 rounded-full bg-white text-slate-900 px-5 py-3 font-semibold">Explore Tracks <ArrowRight className="w-4 h-4"/></SafeLink>
            <SafeLink href="#join" className="inline-flex items-center gap-2 rounded-full border border-white/30 px-5 py-3">Become a Partner</SafeLink>
          </div>
          <div className="mt-6 flex flex-wrap gap-6 text-sm text-indigo-200">
            <span className="inline-flex items-center gap-2"><Calendar className="w-4 h-4"/> Onboarding clinics every month</span>
            <span className="inline-flex items-center gap-2"><BadgeCheck className="w-4 h-4"/> Standardized MOUs & playbooks</span>
            <span className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4"/> Transparent revenue models</span>
          </div>
        </motion.div>
        <motion.div initial={{opacity:0, y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.5, delay:0.05}}>
          <Card className="p-6 md:p-8 bg-white/95">
            <div className="flex items-center gap-3 mb-6">
              <Pill><Network className="w-4 h-4"/> Six Partner Tracks</Pill>
              <span className="text-slate-500">Where you plug in</span>
            </div>
            <ul className="grid sm:grid-cols-2 gap-4">
              {partnerTracks.map(t => (
                <li key={t.key} className="rounded-xl p-4 border border-slate-200 hover:shadow-md transition">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${t.color} grid place-items-center text-white mb-2`}>
                    {React.createElement(t.icon, { className: "w-5 h-5" })}
                  </div>
                  <div className="font-semibold text-slate-900">{t.title}</div>
                  <div className="mt-1 text-xs text-slate-600">{t.value[0]} • {t.value[1]}</div>
                </li>
              ))}
            </ul>
            <div className="mt-6 text-xs text-slate-500">Partners power Communities, Academy, DIGIIT.AI, and Catalyst to create real outcomes.</div>
          </Card>
        </motion.div>
      </div>
    </Section>
  </div>
);

// ================= TRACKS =================
const Tracks = () => (
  <Section id="tracks" className="py-16">
    <H2 kicker="Partner Tracks" center>Six ways to enable the digital ecosystem</H2>
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {partnerTracks.map(t => (
        <motion.div key={t.key} initial={{opacity:0, y:12}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.35}}>
          <Card className="p-6 h-full flex flex-col">
            <div className="flex items-center gap-3">
              <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${t.color} grid place-items-center text-white`}>
                {React.createElement(t.icon, { className: "w-5 h-5" })}
              </div>
              <div className="text-xl font-bold text-slate-900">{t.title}</div>
            </div>
            <ul className="mt-4 space-y-2 text-sm text-slate-700">
              {t.value.map((v, i) => (
                <li key={i} className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-indigo-600 mt-0.5"/> {v}</li>
              ))}
            </ul>
            <div className="mt-5 flex gap-3 mt-auto">
              <SafeLink href={`#join?track=${t.key}`} className="rounded-full px-4 py-2 text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700">Apply to {t.title}</SafeLink>
              <SafeLink href="#faq" className="rounded-full px-4 py-2 text-sm font-semibold border border-slate-300 hover:bg-slate-50">Learn more</SafeLink>
            </div>
          </Card>
        </motion.div>
      ))}
    </div>
  </Section>
);

// ================= ECOSYSTEM FLOW (Partners power 4 pillars) =================
const Ecosystem = () => (
  <Section id="ecosystem" className="py-16">
    <H2 kicker="How Partners Create Impact" center>From intent → onboarding → outcomes</H2>
    <div className="relative mt-6">
      {/* Diagram container */}
      <div className="grid md:grid-cols-3 gap-6">
        <Card className="p-6 md:col-span-1">
          <div className="text-sm uppercase tracking-wider text-slate-500">Start</div>
          <div className="mt-1 text-xl font-bold text-slate-900">Partner Hub</div>
          <p className="mt-2 text-sm text-slate-700">Standards, MOUs, toolkits, and governance rails for all partner tracks.</p>
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <Pill><ShieldCheck className="w-4 h-4"/> Governance</Pill>
            <Pill><Target className="w-4 h-4"/> Playbooks</Pill>
            <Pill><Sparkles className="w-4 h-4"/> Enablement</Pill>
          </div>
        </Card>
        <div className="md:col-span-2 grid sm:grid-cols-2 gap-6">
          <Card className="p-6">
            <div className="flex items-center gap-2 font-semibold text-slate-900"><Globe2 className="w-5 h-5"/> Communities</div>
            <div className="mt-1 text-sm text-slate-600">Chapters, cohorts, and awareness → demand generation & inclusion.</div>
            <SafeLink href="#" className="mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold border border-slate-300 hover:bg-slate-50">View Communities</SafeLink>
          </Card>
          <Card className="p-6">
            <div className="flex items-center gap-2 font-semibold text-slate-900"><GraduationCap className="w-5 h-5"/> Academy</div>
            <div className="mt-1 text-sm text-slate-600">Courses, certifications, trainer network, and job pathways.</div>
            <SafeLink href="#" className="mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold border border-slate-300 hover:bg-slate-50">View Academy</SafeLink>
          </Card>
          <Card className="p-6">
            <div className="flex items-center gap-2 font-semibold text-slate-900"><Building2 className="w-5 h-5"/> DIGIIT.AI Cloud</div>
            <div className="mt-1 text-sm text-slate-600">ONDC/ULIP/OCEN/DSEP rails, apps, and enterprise platforms.</div>
            <SafeLink href="#" className="mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold border border-slate-300 hover:bg-slate-50">View DIGIIT.AI</SafeLink>
          </Card>
          <Card className="p-6">
            <div className="flex items-center gap-2 font-semibold text-slate-900"><Handshake className="w-5 h-5"/> Catalyst</div>
            <div className="mt-1 text-sm text-slate-600">Mentors, trainers, corporate linkage, and impact programs.</div>
            <SafeLink href="#" className="mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold border border-slate-300 hover:bg-slate-50">View Catalyst</SafeLink>
          </Card>
        </div>
      </div>
      {/* Simple connector line for visual continuity */}
      <div className="hidden md:block absolute left-1/2 top-28 bottom-12 w-1 bg-indigo-100 -translate-x-1/2"/>
    </div>
  </Section>
);

// ================= BENEFITS =================
const Benefits = () => (
  <Section id="benefits" className="py-16">
    <H2 kicker="Why Partner" center>Social + economic value with sustainable revenue</H2>
    <div className="grid md:grid-cols-3 gap-6">
      {[
        { title: "Nation-building", desc: "Drive DPI adoption across districts, sectors, and communities.", icon: Landmark },
        { title: "Sustainable Revenue", desc: "Programs with commissions, renewals, and co-branded offerings.", icon: ShieldCheck },
        { title: "Network Effects", desc: "Access to banks, logistics, SaaS, and policy partners.", icon: Network },
      ].map((b) => (
        <Card key={b.title} className="p-6 text-center">
          {React.createElement(b.icon, { className: "w-10 h-10 text-indigo-600 mx-auto" })}
          <div className="mt-3 font-semibold text-slate-900">{b.title}</div>
          <div className="text-sm text-slate-600">{b.desc}</div>
        </Card>
      ))}
    </div>
  </Section>
);

// ================= REVENUE (placeholder levers) =================
const Revenue = () => (
  <Section id="revenue" className="py-16">
    <H2 kicker="Incentives" center>Example revenue levers (configure later)</H2>
    <div className="grid md:grid-cols-3 gap-6">
      {[
        { name: "Workshops & Cohorts", lever: "Per-seat fees + completion bonuses" },
        { name: "Deployments", lever: "One-time setup + % NSV or fixed platform fee" },
        { name: "Renewals", lever: "Annual renewals with partner share" },
      ].map((p) => (
        <Card key={p.name} className="p-6">
          <div className="text-sm uppercase tracking-wider text-slate-500">{p.name}</div>
          <div className="mt-2 text-slate-700 text-sm">{p.lever}</div>
        </Card>
      ))}
    </div>
    <div className="mt-6 text-center text-xs text-slate-500">Note: Tie exact percentages to your Commission Policy & Pricebook guardrails.</div>
  </Section>
);

// ================= STORIES =================
const Stories = () => (
  <Section id="stories" className="py-16">
    <H2 kicker="Impact" center>Success stories & case snippets</H2>
    <div className="grid md:grid-cols-2 gap-6">
      {stories.map(s => (
        <motion.div key={s.name} initial={{opacity:0, y:12}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.35}}>
          <Card className="p-6">
            <div className="font-semibold text-slate-900">{s.name}</div>
            <div className="text-sm text-slate-500">{s.result}</div>
            <p className="mt-2 text-sm text-slate-700">{s.blurb}</p>
          </Card>
        </motion.div>
      ))}
    </div>
  </Section>
);

// ================= EVENTS =================
const Events = () => (
  <Section id="events" className="py-16">
    <H2 kicker="Events" center>Upcoming partner clinics & roundtables</H2>
    <div className="grid md:grid-cols-2 gap-4">
      {events.map(e => (
        <Card key={e.title} className="p-5">
          <div className="flex items-center gap-2 text-slate-500 text-sm"><Calendar className="w-4 h-4"/> {new Date(e.date).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" })}</div>
          <div className="mt-2 font-semibold text-slate-900">{e.title}</div>
          <div className="mt-3 flex gap-2">
            <SafeLink href="#join" className="rounded-full px-3 py-1.5 text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700">RSVP</SafeLink>
            <SafeLink href="#" className="rounded-full px-3 py-1.5 text-sm font-semibold border border-slate-300 hover:bg-slate-50">Details</SafeLink>
          </div>
        </Card>
      ))}
    </div>
  </Section>
);

// ================= JOIN FORM =================
const Join = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    org: "",
    website: "",
    city: "",
    track: partnerTracks[0].key,
    focus: "Communities",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const onSubmit = (e) => {
    e.preventDefault();
    if(!form.name || !form.email) { alert("Please fill name & email"); return; }
    setSubmitted(true);
  };
  return (
    <div id="join" className="bg-gradient-to-b from-slate-50 to-white border-t border-slate-200">
      <Section className="py-16">
        <H2 kicker="Apply" center>Become a ONE Partner</H2>
        <div className="grid lg:grid-cols-3 gap-8">
          <Card className="p-6 lg:col-span-2">
            <form onSubmit={onSubmit} className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-600">Full name</label>
                <input name="name" value={form.name} onChange={onChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Your name"/>
              </div>
              <div>
                <label className="text-sm text-slate-600">Email</label>
                <input name="email" value={form.email} onChange={onChange} type="email" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="you@email.com"/>
              </div>
              <div>
                <label className="text-sm text-slate-600">Phone</label>
                <input name="phone" value={form.phone} onChange={onChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="+91‑"/>
              </div>
              <div>
                <label className="text-sm text-slate-600">Organization</label>
                <input name="org" value={form.org} onChange={onChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Your org / institution"/>
              </div>
              <div>
                <label className="text-sm text-slate-600">Website</label>
                <input name="website" value={form.website} onChange={onChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="https://"/>
              </div>
              <div>
                <label className="text-sm text-slate-600">City / Region</label>
                <input name="city" value={form.city} onChange={onChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Your city"/>
              </div>
              <div>
                <label className="text-sm text-slate-600">Partner Track</label>
                <select name="track" value={form.track} onChange={onChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  {partnerTracks.map(t => <option key={t.key} value={t.key}>{t.title}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm text-slate-600">Primary Focus</label>
                <select name="focus" value={form.focus} onChange={onChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  {["Communities", "Academy", "DIGIIT.AI Cloud", "Catalyst", "Multi"].map(x => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm text-slate-600">Message</label>
                <textarea name="message" value={form.message} onChange={onChange} rows={4} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Tell us about your capabilities and goals"/>
              </div>
              <div className="sm:col-span-2 flex flex-wrap gap-3 items-center">
                <button className="rounded-full bg-indigo-600 text-white px-5 py-3 font-semibold hover:bg-indigo-700" type="submit">Submit Application</button>
                {submitted && <span className="text-sm text-green-600 inline-flex items-center gap-2"><CheckCircle className="w-4 h-4"/> Submitted! We will get back to you soon.</span>}
              </div>
            </form>
          </Card>
          <div className="space-y-4">
            <Card className="p-6">
              <div className="font-semibold text-slate-900">What partners get</div>
              <ul className="mt-2 text-sm text-slate-700 list-disc pl-5 space-y-1">
                <li>Co-branding, listings, and national visibility</li>
                <li>Shared playbooks, toolkits, and trainer/mentor networks</li>
                <li>Revenue share on programs, deployments, and renewals</li>
              </ul>
            </Card>
            <Card className="p-6">
              <div className="font-semibold text-slate-900">Partner Desk</div>
              <div className="mt-2 text-sm text-slate-600">partners@digiit.ai • +91‑00000‑00000 • Coimbatore • Chennai • Bengaluru</div>
              <button onClick={()=>window.print()} className="mt-4 rounded-full px-4 py-2 border border-slate-300 hover:bg-slate-50 text-sm font-semibold">Print / Save PDF</button>
            </Card>
          </div>
        </div>
      </Section>
    </div>
  );
};

// ================= FAQ =================
const FAQ = () => {
  const items = [
    { q: "Who can become a partner?", a: "Academic institutions, incubators, consultants, NGOs/CSOs, influencers/affiliates, and corporates aligned with DPI outcomes." },
    { q: "Is there a fee?", a: "Joining is free. Some programs, certifications, or enterprise offerings may have fees or revenue-sharing MOUs." },
    { q: "How are incentives handled?", a: "Through program-wise commission and renewal policies governed by Pricebook & Guardrail rules." },
    { q: "How do partners interact with the four pillars?", a: "Partners power awareness (Communities), skilling (Academy), deployments (DIGIIT.AI), and enablement (Catalyst)." },
  ];
  const [open, setOpen] = useState(0);
  return (
    <Section id="faq" className="py-16">
      <H2 kicker="FAQ" center>Answers to common questions</H2>
      <div className="max-w-3xl mx-auto space-y-3">
        {items.map((f, i) => (
          <Card key={f.q} className="p-4">
            <button className="w-full flex items-center justify-between text-left" onClick={()=>setOpen(i===open? -1 : i)} aria-expanded={open===i}>
              <span className="font-semibold text-slate-900">{f.q}</span>
              {open===i ? <ChevronUp className="w-5 h-5 text-slate-500"/> : <ChevronDown className="w-5 h-5 text-slate-500"/>}
            </button>
            {open===i && <div className="mt-2 text-sm text-slate-600">{f.a}</div>}
          </Card>
        ))}
      </div>
    </Section>
  );
};

// ================= FOOTER =================
const Footer = () => (
  <footer className="bg-white border-t border-slate-200 text-slate-600">
    <Section className="py-10">
      <div className="grid md:grid-cols-4 gap-8">
        <div>
          <div className="flex items-center gap-2 font-extrabold text-slate-900 text-lg">
            <span className="inline-grid place-items-center w-8 h-8 rounded-xl bg-indigo-600 text-white font-black">P</span>
            Partner Network
          </div>
          <p className="mt-3 text-sm text-slate-500 max-w-sm">Gateway for partners powering Communities, Academy, DIGIIT.AI, and Catalyst.</p>
        </div>
        <div className="text-sm">
          <div className="font-semibold text-slate-900">Quick Links</div>
          <ul className="mt-2 space-y-1">
            <li><SafeLink href="#tracks" className="hover:text-slate-900">Tracks</SafeLink></li>
            <li><SafeLink href="#ecosystem" className="hover:text-slate-900">Ecosystem</SafeLink></li>
            <li><SafeLink href="#events" className="hover:text-slate-900">Events</SafeLink></li>
            <li><SafeLink href="#join" className="hover:text-slate-900">Apply</SafeLink></li>
          </ul>
        </div>
        <div className="text-sm">
          <div className="font-semibold text-slate-900">Pillars</div>
          <ul className="mt-2 space-y-1">
            <li><SafeLink href="#" className="hover:text-slate-900">Communities</SafeLink></li>
            <li><SafeLink href="#" className="hover:text-slate-900">Academy</SafeLink></li>
            <li><SafeLink href="#" className="hover:text-slate-900">DIGIIT.AI</SafeLink></li>
            <li><SafeLink href="#" className="hover:text-slate-900">Catalyst</SafeLink></li>
          </ul>
        </div>
        <div className="text-sm">
          <div className="font-semibold text-slate-900">Contact</div>
          <ul className="mt-2 space-y-1">
            <li className="inline-flex items-center gap-2"><Mail className="w-4 h-4"/> partners@digiit.ai</li>
            <li className="inline-flex items-center gap-2"><Phone className="w-4 h-4"/> +91‑00000‑00000</li>
            <li className="inline-flex items-center gap-2"><MapPin className="w-4 h-4"/> Coimbatore • Chennai • Bengaluru</li>
          </ul>
        </div>
      </div>
      <div className="mt-8 border-t border-slate-200 pt-6 text-xs text-slate-500">© {new Date().getFullYear()} ONE • DIGIIT.AI. All rights reserved.</div>
    </Section>
  </footer>
);

// ================= DEV DIAGNOSTICS (lightweight runtime tests) =================
const DevDiagnostics = () => {
  useEffect(() => {
    try {
      console.assert(Array.isArray(partnerTracks) && partnerTracks.length === 6, 'Expected 6 partner tracks');
      const h2 = document.querySelector('#ecosystem h2')?.textContent || '';
      console.assert(h2.toLowerCase().includes('onboarding') && h2.toLowerCase().includes('outcomes'), 'Ecosystem H2 should describe flow');
      console.assert(events.every(e => !isNaN(Date.parse(e.date))), 'Events should have valid ISO dates');
    } catch (err) {
      console.warn('Diagnostics error', err);
    }
  }, []);
  return null;
};

// ================= OPTIONAL: HashRouter-lite =================
export function useHashRoute(targetHash = "#/partners") {
  const [match, setMatch] = useState(typeof window !== 'undefined' && window.location.hash === targetHash);
  useEffect(() => {
    const fn = () => setMatch(window.location.hash === targetHash);
    window.addEventListener("hashchange", fn);
    return () => window.removeEventListener("hashchange", fn);
  }, [targetHash]);
  return match;
}

// ================= PAGE =================
export default function PartnerNetworkPage() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Nav/>
      <Hero/>
      <Tracks/>
      <Ecosystem/>
      <Benefits/>
      <Revenue/>
      <Stories/>
      <Events/>
      <Join/>
      <FAQ/>
      <Footer/>
      <DevDiagnostics/>
    </div>
  );
}
